import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Unsubscribe as UnsubscribeIcon,
  NotificationAdd as NotificationAddIcon
} from '@mui/icons-material';
import MetaTags from '../components/SEO/MetaTags';
import { getApiUrl } from '../config/api';

// Use logo from public folder
const logo = process.env.PUBLIC_URL + '/logo.png';

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [action, setAction] = useState('unsubscribe'); // 'unsubscribe' or 'resubscribe'

  // Get URL parameters
  const userId = searchParams.get('user_id');
  const token = searchParams.get('token');
  const emailType = searchParams.get('email_type');

  // Email type configurations
  const emailTypeConfig = {
    'welcome': {
      label: 'Welcome Emails',
      description: 'Welcome messages when joining SurveySlack'
    },
    'getting-started': {
      label: 'Getting Started Emails',
      description: 'Helpful tips for creating your first survey'
    },
    'tips-examples': {
      label: 'Tips & Examples',
      description: 'Survey creation tips and examples'
    },
    'final-encouragement': {
      label: 'Encouragement Emails',
      description: 'Final reminders to get started'
    },
    'survey-created': {
      label: 'Survey Creation Notifications',
      description: 'Confirmations when you create new surveys'
    },
    'first-response': {
      label: 'First Response Notifications',
      description: 'Alerts when your surveys receive their first response'
    },
    'response-milestone': {
      label: 'Milestone Celebrations',
      description: 'Celebrations when your surveys reach response milestones'
    },
    'weekly-summary': {
      label: 'Weekly Summaries',
      description: 'Weekly reports on your survey performance'
    },
    're-engagement-14': {
      label: '2-Week Re-engagement',
      description: 'Reminders after 2 weeks of inactivity'
    },
    're-engagement-30': {
      label: '1-Month Re-engagement',
      description: 'Updates after 1 month of inactivity'
    },
    're-engagement-60': {
      label: '2-Month Re-engagement',
      description: 'Final re-engagement after 2 months'
    },
    'survey-completion': {
      label: 'Survey Completion Emails',
      description: 'Thank you emails for survey respondents'
    },
    'survey-reminder': {
      label: 'Survey Reminders',
      description: 'Reminders for incomplete survey responses'
    },
    'marketing': {
      label: 'Marketing Emails',
      description: 'Product updates and promotional content'
    }
  };

  useEffect(() => {
    if (!userId || !token) {
      setError('Invalid unsubscribe link. Missing required parameters.');
      setLoading(false);
      return;
    }

    validateTokenAndLoadPreferences();
  }, [userId, token]);

  const validateTokenAndLoadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, try to get user preferences to validate the token
      const preferencesResponse = await fetch(
        `${getApiUrl('/get_email_preferences.php')}?user_id=${encodeURIComponent(userId)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const preferencesData = await preferencesResponse.json();

      if (preferencesData.status === 'success') {
        // Now validate the unsubscribe token by making a test request
        const tokenResponse = await fetch(
          `${getApiUrl('/unsubscribe.php')}?user_id=${encodeURIComponent(userId)}&token=${encodeURIComponent(token)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const tokenData = await tokenResponse.json();

        if (tokenData.status === 'error' && tokenData.message.includes('Invalid or expired unsubscribe token')) {
          throw new Error('Invalid or expired unsubscribe link');
        }

        // If we get here, the token is valid
        setPreferences(preferencesData.data.preferences);
        
        // If specific email type, pre-select it
        if (emailType && emailTypeConfig[emailType]) {
          setSelectedTypes([emailType]);
        }
      } else {
        throw new Error('Unable to load user preferences');
      }
    } catch (err) {
      console.error('Error validating token:', err);
      setError(err.message || 'Failed to validate unsubscribe link. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    try {
      setProcessing(true);
      setError(null);

      const requestData = {
        user_id: userId,
        token: token,
        action: 'unsubscribe'
      };

      // If specific types selected, unsubscribe from those only
      if (selectedTypes.length > 0 && selectedTypes.length < Object.keys(emailTypeConfig).length) {
        // For multiple types, we need to make multiple requests
        const promises = selectedTypes.map(type => 
          fetch(getApiUrl('/unsubscribe.php'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...requestData,
              email_type: type
            }),
          })
        );

        const responses = await Promise.all(promises);
        const results = await Promise.all(responses.map(r => r.json()));
        
        const failed = results.filter(r => r.status !== 'success');
        if (failed.length > 0) {
          throw new Error('Some email types could not be unsubscribed');
        }
      } else {
        // Unsubscribe from all emails
        const response = await fetch(getApiUrl('/unsubscribe.php'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });

        const data = await response.json();
        if (data.status !== 'success') {
          throw new Error(data.message || 'Failed to unsubscribe');
        }
      }

      setSuccess(true);
      setAction('unsubscribe');
    } catch (err) {
      console.error('Error unsubscribing:', err);
      setError(err.message || 'Failed to unsubscribe. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleResubscribe = async () => {
    try {
      setProcessing(true);
      setError(null);

      const response = await fetch(getApiUrl('/unsubscribe.php'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          token: token,
          action: 'resubscribe'
        }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setSuccess(true);
        setAction('resubscribe');
        setPreferences(data.data.preferences);
      } else {
        throw new Error(data.message || 'Failed to resubscribe');
      }
    } catch (err) {
      console.error('Error resubscribing:', err);
      setError(err.message || 'Failed to resubscribe. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleTypeSelection = (type, checked) => {
    setSelectedTypes(prev => {
      if (checked) {
        return [...prev, type];
      } else {
        return prev.filter(t => t !== type);
      }
    });
  };

  if (loading) {
    return (
      <>
        <MetaTags
          title="Unsubscribe - SurveySlack"
          description="Manage your email preferences and unsubscribe from SurveySlack emails."
          canonical="/unsubscribe"
        />
        <Box sx={{ 
          bgcolor: 'background.default',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Container maxWidth="sm">
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Loading...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Validating your unsubscribe request
              </Typography>
            </Paper>
          </Container>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <MetaTags
          title="Unsubscribe Error - SurveySlack"
          description="There was an error processing your unsubscribe request."
          canonical="/unsubscribe"
        />
        <Box sx={{ 
          bgcolor: 'background.default',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Container maxWidth="sm">
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Unsubscribe Error
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {error}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                startIcon={<ArrowBackIcon />}
              >
                Return to SurveySlack
              </Button>
            </Paper>
          </Container>
        </Box>
      </>
    );
  }

  if (success) {
    return (
      <>
        <MetaTags
          title={`${action === 'unsubscribe' ? 'Unsubscribed' : 'Resubscribed'} - SurveySlack`}
          description={`You have successfully ${action === 'unsubscribe' ? 'unsubscribed from' : 'resubscribed to'} SurveySlack emails.`}
          canonical="/unsubscribe"
        />
        <Box sx={{ 
          bgcolor: 'background.default',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Container maxWidth="sm">
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {action === 'unsubscribe' ? 'Successfully Unsubscribed' : 'Successfully Resubscribed'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {action === 'unsubscribe' 
                  ? 'You have been unsubscribed from the selected email types. You can resubscribe at any time from your profile settings.'
                  : 'You have been resubscribed to SurveySlack emails. You can manage your preferences from your profile settings.'
                }
              </Typography>
              
              {action === 'unsubscribe' && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleResubscribe}
                    disabled={processing}
                    startIcon={<NotificationAddIcon />}
                    sx={{ mr: 2 }}
                  >
                    Resubscribe
                  </Button>
                </Box>
              )}
              
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                startIcon={<ArrowBackIcon />}
              >
                Return to SurveySlack
              </Button>
            </Paper>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <MetaTags
        title="Unsubscribe - SurveySlack"
        description="Manage your email preferences and unsubscribe from SurveySlack emails."
        canonical="/unsubscribe"
      />
      <Box sx={{ 
        bgcolor: 'background.default',
        minHeight: '100vh',
        py: 4
      }}>
        <Container maxWidth="md">
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img 
              src={logo} 
              alt="SurveySlack Logo" 
              style={{ height: '48px', marginBottom: '16px' }}
            />
            <Typography variant="h4" gutterBottom>
              Email Preferences
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage which emails you receive from SurveySlack
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1 }} />
                Choose Email Types
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select the types of emails you want to unsubscribe from, or leave all unchecked to unsubscribe from all emails.
              </Typography>

              <FormGroup>
                {Object.entries(emailTypeConfig).map(([type, config]) => (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox
                        checked={selectedTypes.includes(type)}
                        onChange={(e) => handleTypeSelection(type, e.target.checked)}
                        disabled={processing}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {config.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {config.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ mb: 1, alignItems: 'flex-start' }}
                  />
                ))}
              </FormGroup>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleUnsubscribe}
                  disabled={processing}
                  startIcon={processing ? <CircularProgress size={16} /> : <UnsubscribeIcon />}
                  size="large"
                >
                  {processing ? 'Processing...' : 'Unsubscribe'}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => navigate('/')}
                  disabled={processing}
                  startIcon={<ArrowBackIcon />}
                  size="large"
                >
                  Cancel
                </Button>
              </Box>

              <Box sx={{ 
                mt: 3, 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                textAlign: 'center'
              }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Note:</strong> You can always resubscribe or manage your email preferences 
                  from your profile settings when logged in to SurveySlack.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default UnsubscribePage;